/**
 * uc-core response envelope (src/common/interceptors/transform.interceptor.ts and
 * src/common/interfaces/api-response.interface.ts). Every JSON response is wrapped:
 *
 *   success → { success: true, statusCode, message, data, timestamp, path }
 *   paged   → { success: true, statusCode, message, data: T[], meta, timestamp, path }
 *   error   → { success: false, statusCode, message, errorCode, errors?, timestamp, path, method? }
 */

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface ApiSuccessEnvelope<T> {
  success: true
  statusCode: number
  message: string
  data: T
  timestamp: string
  path: string
}

export interface ApiPaginatedEnvelope<T> extends ApiSuccessEnvelope<T[]> {
  meta: PaginationMeta
}

export interface ApiValidationIssue {
  field: string
  message: string
  value?: unknown
}

export interface ApiErrorEnvelope {
  success: false
  statusCode: number
  message: string
  errorCode: string
  errors?: ApiValidationIssue[] | unknown
  timestamp?: string
  path?: string
  method?: string
}

export interface Paginated<T> {
  data: T[]
  meta: PaginationMeta
  /** Totals over the whole filtered list, when the endpoint sends them (e.g. stock on hand). */
  summary?: Record<string, unknown>
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

export function isSuccessEnvelope(body: unknown): body is ApiSuccessEnvelope<unknown> {
  return isObject(body) && body.success === true && 'data' in body
}

export function isErrorEnvelope(body: unknown): body is ApiErrorEnvelope {
  return isObject(body) && body.success === false && typeof body.message === 'string'
}

export function isPaginatedEnvelope(body: unknown): body is ApiPaginatedEnvelope<unknown> {
  return (
    isSuccessEnvelope(body) &&
    Array.isArray(body.data) &&
    isObject((body as { meta?: unknown }).meta)
  )
}

/** Unwrap `data` from a success envelope; pass through bodies that are not enveloped. */
export function unwrap<T>(body: unknown): T {
  return (isSuccessEnvelope(body) ? body.data : body) as T
}
